import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layout/Layout";
import { useAuth } from "../../components/context/AuthContext";
import { getDashboardDocumentExpirySummary } from "../../api/dashboard/dashboardApi";

const COLORS = {
  navy: "#102744",
  darkNavy: "#071A33",
  blue: "#2F6F98",
  softBlue: "#EAF3F7",
  background: "#F5F7FA",
  card: "#FFFFFF",
  border: "#D9E1EA",
  text: "#172B43",
  muted: "#647084",
  softText: "#8A95A6",
  green: "#2F8F67",
  orange: "#E9962A",
  red: "#D64545",
  purple: "#7257B5",
};

/*
  NOT:
  Route ve permission isimleri tek noktada tutuluyor.
  Projedeki gerçek permission key farklıysa yalnızca aşağıdaki dizilerden değiştirmen yeterli.

  permissions dizisi boş bırakılırsa kart tüm giriş yapmış kullanıcılara görünür.
  Birden fazla permission verilirse kullanıcının bunlardan birine sahip olması yeterlidir.
*/
const OPERATION_ITEMS = [
  {
    key: "overtime-tracking",
    title: "Mesai Takibi",
    description:
      "Personel mesailerini kaydedin, aylık saatleri ve departman dağılımını takip edin.",
    route: "/mesai-takip",
    permissions: ["overtime_viewOwn"],
    accent: "#2F6F98",
    background: "#EAF3F7",
    icon: "clock",
    priority: true,
  },
  {
    key: "overtime-analysis",
    title: "Mesai Analizi",
    description:
      "Aylık ve yıllık mesai eğilimlerini, talep eden departmanları ve görevlendirme nedenlerini inceleyin.",
    route: "/mesai-analiz",
    permissions: ["overtime_analysis"],
    accent: "#2F8F67",
    background: "#EAF7F1",
    icon: "clock",
    priority: true,
  },
  {
    key: "machine-speed",
    title: "Makine Hızları",
    description:
      "Hat ve makinelerin güncel hız bilgilerini görüntüleyin, üretim performansını takip edin.",
    route: "/ScadaDashboard",
    permissions: [
      "makina_hizi_view",
      "makina_uretim_analiz_view",
      "uretim_viewOwn",
    ],
    accent: "#1769AA",
    background: "#EAF4FF",
    icon: "speed",
    priority: true,
  },
  {
    key: "production-entry",
    title: "Üretim Veri Girişi",
    description:
      "Vardiya ve makine bazlı üretim verilerini sisteme girin ve kayıtları takip edin.",
    route: "/makinaUretimAnaliz",
    permissions: [
      "uretim_veri_giris",
      "makina_uretim_create",
      "uretim_viewOwn",
    ],
    accent: "#D46B08",
    background: "#FFF7E8",
    icon: "production",
    priority: true,
  },
  {
    key: "customer-stock-mail",
    title: "Müşteri Stok Maili",
    description:
      "Yüklenen rulo stok raporunu formatlı şekilde hazırlayın ve ilgili müşterilere e-posta olarak gönderin.",
    route: "/RuloStokSatisGiris",
    permissions: ["satis_viewOwn"],
    accent: "#A8071A",
    background: "#FFF1F0",
    icon: "mail",
    priority: true,
  },
  {
    key: "maintenance",
    title: "Bakım Yönetimi",
    description:
      "Planlı ve arızi bakım kayıtlarını görüntüleyin, iş emirlerini ve bakım durumlarını yönetin.",
    route: "/equipmentPlanManagement",
    permissions: ["bakim_viewOwn", "bakim_view", "maintenance_view"],
    accent: "#2F8F67",
    background: "#EAF7F1",
    icon: "maintenance",
    priority: true,
  },
  {
    key: "payment-list",
    title: "Ödeme Listesi",
    description:
      "Bekleyen, onaylanan ve tamamlanan ödeme kayıtlarını tek ekrandan görüntüleyin.",
    route: "/PaymentDueListDemo",
    permissions: ["payment_list_view", "payment_view"],
    accent: "#531DAB",
    background: "#F9F0FF",
    icon: "payment",
    priority: true,
  },
  {
    key: "shipment-form",
    title: "Sevkiyat Formu",
    description:
      "Yükleme kontrol formlarını görüntüleyin, düzenleyin ve tamamlayın.",
    route: "/sevkiyat/yukleme-kontrol",
    permissions: ["sevkiyat_view", "sevkiyat_form_view"],
    accent: "#1677FF",
    background: "#EEF6FF",
    icon: "shipment",
  },
  {
    key: "scale-photos",
    title: "Kantar Fotoğrafları",
    description:
      "Kantar giriş fotoğraflarını oluşturun ve sevkiyat kayıtlarına aktarın.",
    route: "/sevkiyat/kantar-fotograf",
    permissions: ["kantar_fotograf_view", "sevkiyat_view"],
    accent: "#722ED1",
    background: "#F7F0FF",
    icon: "camera",
  },
];

const QUALITY_ITEMS = [
  {
    key: "quality-file-management",
    title: "Kalite Dosya Yönetimi",
    description:
      "Kalite dosyalarını, kontrol dokümanlarını ve kalite süreç kayıtlarını yönetin.",
    route: "/QualityFileManagerPage",
    permissions: [
      "quality_file_manager_view",
      "kalite_viewOwn",
      "quality_view",
    ],
    accent: "#237804",
    background: "#F6FFED",
    icon: "quality",
    badge: "Demo",
    priority: true,
  },
];

const DEMO_PROCESS_ITEMS = [
  {
    key: "dof-demo",
    title: "DÖF Süreçleri",
    description:
      "İlgili birimlere yönlendirilen düzeltici ve önleyici faaliyetlerin örnek durum dağılımı.",
    icon: "approval",
    accent: "#C41D7F",
    background: "#FFF0F6",
    stats: [
      { label: "Onay Bekliyor", value: 6, tone: "warning" },
      { label: "Onaylandı", value: 18, tone: "success" },
      { label: "İade Edildi", value: 2, tone: "danger" },
    ],
  },
  {
    key: "complaint-demo",
    title: "Müşteri Şikâyetleri",
    description:
      "Kalite tarafından ilgili departmanlara aktarılan müşteri şikâyetlerinin örnek takibi.",
    icon: "complaint",
    accent: "#CF1322",
    background: "#FFF1F0",
    stats: [
      { label: "Yeni Kayıt", value: 4, tone: "danger" },
      { label: "İşlemde", value: 9, tone: "warning" },
      { label: "Kapatıldı", value: 27, tone: "success" },
    ],
  },
  {
    key: "approval-demo",
    title: "Yönetici Onayları",
    description:
      "Kullanıcı ve yönetici arasındaki örnek onay mekanizmasının güncel durum özeti.",
    icon: "check",
    accent: "#08979C",
    background: "#E6FFFB",
    stats: [
      { label: "Onay Bekliyor", value: 8, tone: "warning" },
      { label: "Onaylandı", value: 31, tone: "success" },
      { label: "Reddedildi", value: 3, tone: "danger" },
    ],
  },
];

const EMPTY_DOCUMENT_EXPIRY_SUMMARY = {
  warningDays: 30,
  authorizedDepartments: [],
  expiredCount: 0,
  approachingCount: 0,
  totalActionRequired: 0,
  expired: [],
  approaching: [],
};

const COMMON_DOCUMENT_ITEM = {
  key: "shared-documents",
  title: "Departmanlar Arası Ortak Dokümanlar",
  description:
    "Tüm yetkili birimlerin ortak kullanımına açılan kurumsal dosya ve dokümanlara ulaşın.",
  route: "/shareddocumentall",
  permissions: ["document_shared_all"],
  accent: "#1769AA",
  background: "#EAF4FF",
  icon: "shared",
  featured: true,
};

const DEPARTMENT_DOCUMENT_ITEMS = [
  {
    key: "quality-documents",
    group: "Kalite",
    title: "Kalite Doküman Arşivi",
    description:
      "Kalite departmanına ait prosedür, talimat, form ve kayıtları görüntüleyin.",
    route: "/quality",
    permissions: ["kalite_viewOwn"],
    accent: "#389E0D",
    background: "#F3FAED",
    icon: "folder",
  },
  {
    key: "quality-shared",
    group: "Kalite",
    title: "Kalite Ortak Dokümanları",
    description:
      "Kalite departmanının diğer birimlerle paylaştığı dokümanlara ulaşın.",
    route: "/ortakKalite",
    permissions: ["kalite_shared_viewOwn"],
    accent: "#237804",
    background: "#F6FFED",
    icon: "shared",
  },
  {
    key: "accounting-documents",
    group: "Muhasebe",
    title: "Muhasebe Doküman Arşivi",
    description:
      "Muhasebe birimine ait mali kayıt, liste ve kurumsal belgeleri görüntüleyin.",
    route: "/accountingDocument",
    permissions: ["muhasebe_viewOwn"],
    accent: "#0958D9",
    background: "#E6F4FF",
    icon: "folder",
  },
  {
    key: "accounting-shared",
    group: "Muhasebe",
    title: "Muhasebe Ortak Dokümanları",
    description:
      "Muhasebe tarafından ortak kullanıma açılan dokümanlara ulaşın.",
    route: "/ortakMuhasebe",
    permissions: ["muhasebe_shared_viewOwn"],
    accent: "#1677FF",
    background: "#F0F5FF",
    icon: "shared",
  },
  {
    key: "isg-documents",
    group: "İSG",
    title: "İSG Doküman Arşivi",
    description:
      "İş sağlığı ve güvenliği birimine ait belge, form ve talimatlara ulaşın.",
    route: "/IsgDocument",
    permissions: ["isg_viewOwn"],
    accent: "#D46B08",
    background: "#FFF7E6",
    icon: "folder",
  },
  {
    key: "isg-shared",
    group: "İSG",
    title: "İSG Ortak Dokümanları",
    description:
      "İSG birimi tarafından paylaşılan ortak dokümanları görüntüleyin.",
    route: "/ortakIsg",
    permissions: ["isg_shared_viewOwn"],
    accent: "#FA8C16",
    background: "#FFF7E8",
    icon: "shared",
  },
  {
    key: "production-documents",
    group: "Üretim",
    title: "Üretim Doküman Arşivi",
    description:
      "Üretim departmanına ait teknik dosya, form ve çalışma dokümanlarını görüntüleyin.",
    route: "/uretimDocument",
    permissions: ["uretim_viewOwn"],
    accent: "#531DAB",
    background: "#F9F0FF",
    icon: "folder",
  },
  {
    key: "production-shared",
    group: "Üretim",
    title: "Üretim Ortak Dokümanları",
    description:
      "Üretim departmanının diğer birimlerle paylaştığı dokümanlara ulaşın.",
    route: "/ortakUretim",
    permissions: ["uretim_shared_viewOwn"],
    accent: "#722ED1",
    background: "#F7F0FF",
    icon: "shared",
  },
  {
    key: "administrative-documents",
    group: "İdari İşler",
    title: "İdari İşler Doküman Arşivi",
    description:
      "İdari işler departmanına ait kurumsal dokümanları görüntüleyin.",
    route: "/administrativeDocument",
    permissions: ["IdariIsler_viewOwn"],
    accent: "#7C4DFF",
    background: "#F5F0FF",
    icon: "folder",
  },
  {
    key: "hr-documents",
    group: "İnsan Kaynakları",
    title: "İnsan Kaynakları Doküman Arşivi",
    description:
      "İnsan kaynakları birimine ait dosya, form ve belgeleri görüntüleyin.",
    route: "/hrDocument",
    permissions: ["InsanKaynaklari_viewOwn"],
    accent: "#C41D7F",
    background: "#FFF0F6",
    icon: "folder",
  },
  {
    key: "hr-shared",
    group: "İnsan Kaynakları",
    title: "İK Ortak Dokümanları",
    description:
      "İnsan kaynakları tarafından paylaşılan ortak dokümanlara ulaşın.",
    route: "/ortakInsanKaynaklari",
    permissions: ["insanKaynaklari_shared_viewOwn"],
    accent: "#EB2F96",
    background: "#FFF0F6",
    icon: "shared",
  },
  {
    key: "foreign-trade-documents",
    group: "Dış Ticaret",
    title: "Dış Ticaret Doküman Arşivi",
    description:
      "Dış ticaret birimine ait müşteri ve operasyon belgelerine ulaşın.",
    route: "/foreignTradeDocument",
    permissions: ["disTicaret_viewOwn"],
    accent: "#0050B3",
    background: "#E6F4FF",
    icon: "folder",
  },
  {
    key: "foreign-trade-shared",
    group: "Dış Ticaret",
    title: "Dış Ticaret Ortak Dokümanları",
    description:
      "Dış ticaret tarafından ortak kullanıma açılan dokümanları görüntüleyin.",
    route: "/ortakDisTicaret",
    permissions: ["disTicaret_shared_viewOwn"],
    accent: "#096DD9",
    background: "#E6F7FF",
    icon: "shared",
  },
  {
    key: "purchasing-documents",
    group: "Satınalma",
    title: "Satınalma Doküman Arşivi",
    description:
      "Satınalma ve depo birimine ait dosya ve dokümanları görüntüleyin.",
    route: "/satinAlmaDocument",
    permissions: ["satinalma_viewOwn"],
    accent: "#AD4E00",
    background: "#FFF7E6",
    icon: "folder",
  },
  {
    key: "purchasing-shared",
    group: "Satınalma",
    title: "Satınalma Ortak Dokümanları",
    description:
      "Satınalma birimi tarafından paylaşılan ortak dokümanlara ulaşın.",
    route: "/ortakSatinAlma",
    permissions: ["satinalma_shared_viewOwn"],
    accent: "#D46B08",
    background: "#FFF7E8",
    icon: "shared",
  },
  {
    key: "maintenance-documents",
    group: "Bakım",
    title: "Bakım Doküman Arşivi",
    description:
      "Bakım departmanına ait teknik dosya, talimat ve kayıtları görüntüleyin.",
    route: "/bakimdocument",
    permissions: ["bakim_viewOwn"],
    accent: "#434343",
    background: "#F5F5F5",
    icon: "folder",
  },
  {
    key: "sales-documents",
    group: "Satış",
    title: "Satış Doküman Arşivi",
    description:
      "Satış departmanına ait müşteri ve operasyon dosyalarını görüntüleyin.",
    route: "/salesDocuments",
    permissions: ["satis_viewOwn"],
    accent: "#A8071A",
    background: "#FFF1F0",
    icon: "folder",
  },
];

function normalizePermission(permission) {
  if (typeof permission === "string") return permission;

  return (
    permission?.key ||
    permission?.name ||
    permission?.permissionKey ||
    permission?.value ||
    ""
  );
}

function getUserPermissions(user) {
  const permissionSources = [
    user?.permissions,
    user?.permissionKeys,
    user?.role?.permissions,
    user?.roleId?.permissions,
    user?.userRole?.permissions,
  ];

  return [
    ...new Set(
      permissionSources
        .filter(Array.isArray)
        .flat()
        .map(normalizePermission)
        .filter(Boolean)
    ),
  ];
}

function hasAnyPermission(userPermissions, requiredPermissions = []) {
  if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) {
    return true;
  }

  return requiredPermissions.some((permission) =>
    userPermissions.includes(permission)
  );
}

function getAuthorizedItems(items, userPermissions) {
  return items.filter((item) =>
    hasAnyPermission(userPermissions, item.permissions)
  );
}

function hasFullDashboardAccess(user, userPermissions) {
  return Boolean(
    user?.isAdmin ||
      user?.isSuperAdmin ||
      user?.role?.isAdmin ||
      user?.roleId?.isAdmin ||
      userPermissions.includes("*") ||
      userPermissions.includes("all_permissions") ||
      userPermissions.includes("dashboard_view_all") ||
      userPermissions.includes("document_expiry_view_all")
  );
}

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documentExpirySummary, setDocumentExpirySummary] = useState(
    EMPTY_DOCUMENT_EXPIRY_SUMMARY
  );
  const [documentExpiryLoading, setDocumentExpiryLoading] = useState(true);
  const [documentExpiryError, setDocumentExpiryError] = useState("");
  const [documentExpiryRefreshKey, setDocumentExpiryRefreshKey] = useState(0);

  const userPermissions = useMemo(() => getUserPermissions(user), [user]);
  const canViewAllDepartments = useMemo(
    () => hasFullDashboardAccess(user, userPermissions),
    [user, userPermissions]
  );

  useEffect(() => {
    let isMounted = true;

    async function fetchDocumentExpirySummary() {
      try {
        setDocumentExpiryLoading(true);
        setDocumentExpiryError("");

        const response = await getDashboardDocumentExpirySummary({
          warningDays: 30,
        });

        const payload = response?.data?.data || response?.data;

        if (!payload) {
          throw new Error("Dashboard süre özeti boş döndü.");
        }

        if (isMounted) {
          const expired = Array.isArray(payload.expired) ? payload.expired : [];
          const approaching = Array.isArray(payload.approaching)
            ? payload.approaching
            : [];

          setDocumentExpirySummary({
            warningDays: Number(payload.warningDays || 30),
            authorizedDepartments: Array.isArray(payload.authorizedDepartments)
              ? payload.authorizedDepartments
              : [],
            expiredCount: Number(payload.expiredCount ?? expired.length),
            approachingCount: Number(
              payload.approachingCount ?? approaching.length
            ),
            totalActionRequired: Number(
              payload.totalActionRequired ?? expired.length + approaching.length
            ),
            expired,
            approaching,
          });
        }
      } catch (error) {
        console.error("Dashboard süre özeti alınamadı:", error);

        if (isMounted) {
          setDocumentExpirySummary(EMPTY_DOCUMENT_EXPIRY_SUMMARY);
          setDocumentExpiryError(
            error?.response?.data?.message ||
              "Süreli doküman bilgileri alınamadı."
          );
        }
      } finally {
        if (isMounted) {
          setDocumentExpiryLoading(false);
        }
      }
    }

    fetchDocumentExpirySummary();

    return () => {
      isMounted = false;
    };
  }, [documentExpiryRefreshKey]);

  const authorizedOperationItems = useMemo(
    () => getAuthorizedItems(OPERATION_ITEMS, userPermissions),
    [userPermissions]
  );

  const authorizedQualityItems = useMemo(
    () => getAuthorizedItems(QUALITY_ITEMS, userPermissions),
    [userPermissions]
  );

  const authorizedDocumentItems = useMemo(
    () => getAuthorizedItems(DEPARTMENT_DOCUMENT_ITEMS, userPermissions),
    [userPermissions]
  );

  const canViewCommonDocuments = useMemo(
    () => hasAnyPermission(userPermissions, COMMON_DOCUMENT_ITEM.permissions),
    [userPermissions]
  );

  const totalAuthorizedModules =
    authorizedOperationItems.length +
    authorizedQualityItems.length +
    authorizedDocumentItems.length +
    (canViewCommonDocuments ? 1 : 0);

  if (!user) return null;

  return (
    <Layout>
      <style>{`
        .corporate-dashboard {
          width: 100%;
          min-height: calc(100vh - 112px);
          box-sizing: border-box;
          color: ${COLORS.text};
        }

        .dashboard-hero {
          position: relative;
          overflow: hidden;
          border-radius: 22px;
          padding: clamp(24px, 4vw, 42px);
          margin-bottom: 22px;
          background:
            radial-gradient(circle at 90% 8%, rgba(255,255,255,.18), transparent 28%),
            linear-gradient(135deg, ${COLORS.darkNavy} 0%, ${COLORS.navy} 58%, ${COLORS.blue} 100%);
          box-shadow: 0 18px 45px rgba(16, 39, 68, 0.16);
        }

        .dashboard-hero::after {
          content: "";
          position: absolute;
          right: -85px;
          bottom: -120px;
          width: 310px;
          height: 310px;
          border: 1px solid rgba(255,255,255,.14);
          border-radius: 50%;
          box-shadow:
            0 0 0 38px rgba(255,255,255,.035),
            0 0 0 76px rgba(255,255,255,.025);
          pointer-events: none;
        }

        .dashboard-hero-content {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 28px;
          flex-wrap: wrap;
        }

        .dashboard-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding: 7px 11px;
          border: 1px solid rgba(255,255,255,.18);
          border-radius: 999px;
          background: rgba(255,255,255,.09);
          color: rgba(255,255,255,.82);
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .75px;
          text-transform: uppercase;
        }

        .dashboard-hero-title {
          margin: 0;
          color: #fff;
          font-size: clamp(25px, 4vw, 38px);
          font-weight: 800;
          line-height: 1.2;
        }

        .dashboard-hero-text {
          max-width: 700px;
          margin: 13px 0 0;
          color: rgba(255,255,255,.72);
          font-size: 15px;
          line-height: 1.75;
        }

        .dashboard-user-panel {
          min-width: 230px;
          padding: 17px 18px;
          border: 1px solid rgba(255,255,255,.16);
          border-radius: 16px;
          background: rgba(255,255,255,.09);
          backdrop-filter: blur(8px);
        }

        .dashboard-user-label {
          color: rgba(255,255,255,.58);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: .45px;
          text-transform: uppercase;
        }

        .dashboard-user-name {
          margin-top: 6px;
          color: #fff;
          font-size: 16px;
          font-weight: 800;
        }

        .dashboard-user-meta {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-top: 10px;
          color: rgba(255,255,255,.68);
          font-size: 12px;
        }

        .dashboard-online-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #52c41a;
          box-shadow: 0 0 0 4px rgba(82,196,26,.14);
        }

        .dashboard-summary-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 22px;
        }

        .dashboard-summary-card {
          display: flex;
          align-items: center;
          gap: 14px;
          min-height: 88px;
          padding: 17px 18px;
          border: 1px solid ${COLORS.border};
          border-radius: 17px;
          background: #fff;
          box-shadow: 0 8px 24px rgba(16,39,68,.045);
        }

        .dashboard-summary-icon {
          flex: 0 0 auto;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 13px;
          background: ${COLORS.softBlue};
          color: ${COLORS.blue};
        }

        .dashboard-summary-value {
          color: ${COLORS.navy};
          font-size: 22px;
          font-weight: 800;
          line-height: 1;
        }

        .dashboard-summary-label {
          margin-top: 5px;
          color: ${COLORS.muted};
          font-size: 12px;
        }

        .dashboard-section {
          padding: clamp(19px, 3vw, 28px);
          margin-bottom: 22px;
          border: 1px solid ${COLORS.border};
          border-radius: 20px;
          background: ${COLORS.card};
          box-shadow: 0 8px 26px rgba(16,39,68,.04);
        }

        .dashboard-section-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 16px;
          margin-bottom: 20px;
        }

        .dashboard-section-title {
          margin: 0;
          color: ${COLORS.navy};
          font-size: 21px;
          font-weight: 800;
        }

        .dashboard-section-description {
          margin: 6px 0 0;
          color: ${COLORS.muted};
          font-size: 13px;
          line-height: 1.5;
        }

        .dashboard-section-count {
          flex: 0 0 auto;
          padding: 6px 10px;
          border-radius: 999px;
          background: ${COLORS.softBlue};
          color: ${COLORS.blue};
          font-size: 11px;
          font-weight: 800;
        }

        .dashboard-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(255px, 1fr));
          gap: 16px;
        }

        .dashboard-card-grid.priority-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .dashboard-card {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          width: 100%;
          min-height: 185px;
          padding: 20px;
          overflow: hidden;
          border: 1px solid ${COLORS.border};
          border-radius: 17px;
          background: #fff;
          text-align: left;
          font-family: inherit;
          cursor: pointer;
          transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
        }

        .dashboard-card::after {
          content: "";
          position: absolute;
          right: -45px;
          bottom: -60px;
          width: 140px;
          height: 140px;
          border-radius: 50%;
          background: var(--card-soft-color);
          opacity: .62;
          pointer-events: none;
        }

        .dashboard-card:hover {
          transform: translateY(-4px);
          border-color: var(--card-accent);
          box-shadow: 0 16px 34px rgba(16,39,68,.10);
        }

        .dashboard-card:focus-visible {
          outline: 3px solid color-mix(in srgb, var(--card-accent) 22%, transparent);
          outline-offset: 2px;
        }

        .dashboard-card-top {
          position: relative;
          z-index: 1;
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }

        .dashboard-card-icon {
          width: 46px;
          height: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          border-radius: 14px;
          color: var(--card-accent);
          background: var(--card-soft-color);
          border: 1px solid color-mix(in srgb, var(--card-accent) 17%, transparent);
        }

        .dashboard-card-badge {
          padding: 5px 8px;
          border-radius: 999px;
          color: var(--card-accent);
          background: var(--card-soft-color);
          font-size: 10px;
          font-weight: 900;
          letter-spacing: .3px;
          text-transform: uppercase;
        }

        .dashboard-card-title {
          position: relative;
          z-index: 1;
          margin-top: 16px;
          color: ${COLORS.navy};
          font-size: 16px;
          font-weight: 800;
          line-height: 1.35;
        }

        .dashboard-card-group {
          position: relative;
          z-index: 1;
          margin-top: 5px;
          color: var(--card-accent);
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .45px;
        }

        .dashboard-card-description {
          position: relative;
          z-index: 1;
          margin-top: 8px;
          padding-right: 8px;
          color: ${COLORS.muted};
          font-size: 13px;
          line-height: 1.58;
        }

        .dashboard-card-action {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: 7px;
          margin-top: auto;
          padding-top: 17px;
          color: var(--card-accent);
          font-size: 12px;
          font-weight: 800;
        }

        .dashboard-empty {
          padding: 28px 20px;
          border: 1px dashed ${COLORS.border};
          border-radius: 16px;
          background: ${COLORS.background};
          color: ${COLORS.muted};
          text-align: center;
          font-size: 13px;
          line-height: 1.6;
        }


        .dashboard-demo-grid,
        .dashboard-expiry-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
        }

        .dashboard-process-card,
        .dashboard-expiry-card {
          position: relative;
          overflow: hidden;
          padding: 20px;
          border: 1px solid ${COLORS.border};
          border-radius: 17px;
          background: #fff;
        }

        .dashboard-process-header,
        .dashboard-expiry-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .dashboard-process-title,
        .dashboard-expiry-title {
          margin-top: 15px;
          color: ${COLORS.navy};
          font-size: 16px;
          font-weight: 800;
        }

        .dashboard-process-description {
          margin-top: 7px;
          color: ${COLORS.muted};
          font-size: 12px;
          line-height: 1.55;
        }

        .dashboard-process-stats {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
          margin-top: 17px;
        }

        .dashboard-process-stat {
          padding: 10px 8px;
          border-radius: 11px;
          background: ${COLORS.background};
          text-align: center;
        }

        .dashboard-process-stat-value {
          color: ${COLORS.navy};
          font-size: 18px;
          font-weight: 900;
        }

        .dashboard-process-stat-label {
          margin-top: 4px;
          color: ${COLORS.muted};
          font-size: 9px;
          font-weight: 800;
          line-height: 1.25;
          text-transform: uppercase;
        }

        .dashboard-process-stat.warning {
          background: #FFF7E8;
        }

        .dashboard-process-stat.success {
          background: #EAF7F1;
        }

        .dashboard-process-stat.danger {
          background: #FFF1F0;
        }

        .dashboard-expiry-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .dashboard-expiry-card.expired {
          border-top: 4px solid ${COLORS.red};
        }

        .dashboard-expiry-card.approaching {
          border-top: 4px solid ${COLORS.orange};
        }

        .dashboard-expiry-count {
          font-size: 30px;
          font-weight: 900;
          line-height: 1;
        }

        .dashboard-expiry-card.expired .dashboard-expiry-count {
          color: ${COLORS.red};
        }

        .dashboard-expiry-card.approaching .dashboard-expiry-count {
          color: ${COLORS.orange};
        }

        .dashboard-expiry-list {
          display: grid;
          gap: 9px;
          margin-top: 16px;
        }

        .dashboard-expiry-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 10px 11px;
          border-radius: 11px;
          background: ${COLORS.background};
        }

        .dashboard-expiry-name {
          color: ${COLORS.text};
          font-size: 12px;
          font-weight: 800;
        }

        .dashboard-expiry-department {
          margin-top: 3px;
          color: ${COLORS.muted};
          font-size: 10px;
        }

        .dashboard-expiry-days {
          flex: 0 0 auto;
          font-size: 11px;
          font-weight: 900;
        }

        .dashboard-expiry-card.expired .dashboard-expiry-days {
          color: ${COLORS.red};
        }

        .dashboard-expiry-card.approaching .dashboard-expiry-days {
          color: ${COLORS.orange};
        }

        .dashboard-demo-note {
          padding: 5px 8px;
          border-radius: 999px;
          background: #FFF7E8;
          color: ${COLORS.orange};
          font-size: 10px;
          font-weight: 900;
          text-transform: uppercase;
        }
        .dashboard-expiry-row-button {
          width: 100%;
          border: 0;
          font-family: inherit;
          text-align: left;
          cursor: pointer;
          transition: transform .18s ease, background .18s ease;
        }

        .dashboard-expiry-row-button:hover {
          transform: translateY(-1px);
          background: #EEF3F8;
        }

        .dashboard-expiry-row-button:focus-visible {
          outline: 2px solid ${COLORS.blue};
          outline-offset: 2px;
        }

        .dashboard-expiry-error {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 16px;
          padding: 13px 14px;
          border: 1px solid #FFD1CC;
          border-radius: 13px;
          background: #FFF4F2;
          color: ${COLORS.red};
          font-size: 12px;
          font-weight: 700;
        }

        .dashboard-retry-button {
          flex: 0 0 auto;
          border: 1px solid ${COLORS.red};
          border-radius: 9px;
          padding: 7px 10px;
          background: #fff;
          color: ${COLORS.red};
          font-family: inherit;
          font-size: 11px;
          font-weight: 800;
          cursor: pointer;
        }

        .dashboard-expiry-departments {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 9px;
        }

        .dashboard-expiry-department-chip {
          padding: 5px 8px;
          border-radius: 999px;
          background: ${COLORS.softBlue};
          color: ${COLORS.blue};
          font-size: 10px;
          font-weight: 800;
        }

        @media (max-width: 960px) {
          .dashboard-summary-grid {
            grid-template-columns: 1fr;
          }

          .dashboard-card-grid.priority-grid,
          .dashboard-demo-grid,
          .dashboard-expiry-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 640px) {
          .dashboard-hero {
            border-radius: 18px;
          }

          .dashboard-user-panel {
            width: 100%;
            min-width: 0;
          }

          .dashboard-section {
            border-radius: 17px;
          }

          .dashboard-section-header {
            align-items: flex-start;
          }

          .dashboard-card-grid {
            grid-template-columns: 1fr;
          }

          .dashboard-card {
            min-height: 168px;
          }
        }
      `}</style>

      <section className="corporate-dashboard">
        <header className="dashboard-hero">
          <div className="dashboard-hero-content">
            <div>
              <div className="dashboard-eyebrow">
                <DashboardIcon name="company" size={15} />
                Kurumsal Operasyon Merkezi
              </div>

              <h1 className="dashboard-hero-title">Hoş geldin, {user.name}</h1>

              <p className="dashboard-hero-text">
                Günlük operasyonlara, kalite süreçlerine, bekleyen onaylara ve
                süresi yaklaşan kurumsal kayıtlara tek ekrandan ulaşabilirsiniz.
              </p>
            </div>

            <div className="dashboard-user-panel">
              <div className="dashboard-user-label">Aktif kullanıcı</div>
              <div className="dashboard-user-name">
                {user.name} {user.surname || ""}
              </div>
              <div className="dashboard-user-meta">
                <span className="dashboard-online-dot" />
                Sistem erişimi aktif
              </div>
            </div>
          </div>
        </header>

        <div className="dashboard-summary-grid">
          <SummaryCard
            icon="modules"
            value={totalAuthorizedModules}
            label="Erişebildiğiniz toplam modül"
          />
          <SummaryCard
            icon="operations"
            value={authorizedOperationItems.length}
            label="Aktif operasyon modülü"
          />
          <SummaryCard
            icon="documents"
            value={
              authorizedDocumentItems.length + (canViewCommonDocuments ? 1 : 0)
            }
            label="Yetkili doküman alanı"
          />
          <SummaryCard
            icon="warning"
            value={documentExpirySummary.totalActionRequired}
            label="Takip gerektiren süreli kayıt"
          />
        </div>

        <DashboardSection
          title="Önemli Operasyonlar"
          description="Günlük iş akışında hızlı erişilmesi gereken üretim, bakım, ödeme ve sevkiyat modülleri"
          items={authorizedOperationItems}
          onNavigate={navigate}
          priority
          emptyText="Yetkili olduğunuz bir operasyon modülü bulunmuyor."
        />

        <DashboardSection
          title="Kalite Dosya Yönetimi"
          description="Kalite dosyaları, kontrol dokümanları ve kalite süreç kayıtları"
          items={authorizedQualityItems}
          onNavigate={navigate}
          emptyText="Kalite dosya yönetimi için yetkiniz bulunmuyor."
        />

        <DemoProcessSection items={DEMO_PROCESS_ITEMS} />

        <DocumentExpirySection
          summary={documentExpirySummary}
          loading={documentExpiryLoading}
          error={documentExpiryError}
          canViewAllDepartments={canViewAllDepartments}
          onRetry={() =>
            setDocumentExpiryRefreshKey((currentValue) => currentValue + 1)
          }
          onNavigate={navigate}
        />

        <DashboardSection
          title="Doküman Merkezi"
          description="Departmanlar arası ortak dokümanlar ve biriminize özel doküman arşivleri"
          items={[
            ...(canViewCommonDocuments ? [COMMON_DOCUMENT_ITEM] : []),
            ...authorizedDocumentItems,
          ]}
          onNavigate={navigate}
          emptyText="Yetkili olduğunuz bir doküman arşivi bulunmuyor."
        />
      </section>
    </Layout>
  );
}

function SummaryCard({ icon, value, label }) {
  return (
    <div className="dashboard-summary-card">
      <div className="dashboard-summary-icon">
        <DashboardIcon name={icon} size={22} />
      </div>
      <div>
        <div className="dashboard-summary-value">{value}</div>
        <div className="dashboard-summary-label">{label}</div>
      </div>
    </div>
  );
}

function DemoProcessSection({ items }) {
  return (
    <section className="dashboard-section">
      <div className="dashboard-section-header">
        <div>
          <h2 className="dashboard-section-title">Süreç ve Onay Takibi</h2>
          <p className="dashboard-section-description">
            DÖF, müşteri şikâyeti ve yönetici onay mekanizması için örnek
            dashboard verileri
          </p>
        </div>
        <div className="dashboard-demo-note">Demo Veri</div>
      </div>

      <div className="dashboard-demo-grid">
        {items.map((item) => (
          <div className="dashboard-process-card" key={item.key}>
            <div className="dashboard-process-header">
              <div
                className="dashboard-card-icon"
                style={{
                  "--card-accent": item.accent,
                  "--card-soft-color": item.background,
                }}
              >
                <DashboardIcon name={item.icon} size={23} />
              </div>
              <span className="dashboard-demo-note">Demo</span>
            </div>

            <div className="dashboard-process-title">{item.title}</div>
            <div className="dashboard-process-description">
              {item.description}
            </div>

            <div className="dashboard-process-stats">
              {item.stats.map((stat) => (
                <div
                  className={`dashboard-process-stat ${stat.tone}`}
                  key={stat.label}
                >
                  <div className="dashboard-process-stat-value">
                    {stat.value}
                  </div>
                  <div className="dashboard-process-stat-label">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DocumentExpirySection({
  summary,
  loading,
  error,
  canViewAllDepartments,
  onRetry,
  onNavigate,
}) {
  const cards = [
    {
      key: "expired",
      title: "Süresi Dolanlar",
      count: summary.expiredCount,
      items: summary.expired,
      emptyText: "Süresi dolan kayıt bulunmuyor.",
    },
    {
      key: "approaching",
      title: "Süresi Yaklaşanlar",
      count: summary.approachingCount,
      items: summary.approaching,
      emptyText: "Süresi yaklaşan kayıt bulunmuyor.",
    },
  ];

  const authorizedDepartments = Array.isArray(summary.authorizedDepartments)
    ? summary.authorizedDepartments
    : [];

  return (
    <section className="dashboard-section">
      <div className="dashboard-section-header">
        <div>
          <h2 className="dashboard-section-title">Süre ve Yenileme Takibi</h2>
          <p className="dashboard-section-description">
            {canViewAllDepartments
              ? "Yönetici görünümü: yetkiniz bulunan tüm departmanlardaki süreli kayıtlar gösteriliyor."
              : "Yalnızca yetkili olduğunuz departmanlardaki süreli kayıtlar gösteriliyor."}
          </p>

          {authorizedDepartments.length > 0 && (
            <div className="dashboard-expiry-departments">
              {authorizedDepartments.map((department) => (
                <span
                  className="dashboard-expiry-department-chip"
                  key={department.key}
                >
                  {department.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {loading && <div className="dashboard-demo-note">Kontrol Ediliyor</div>}
      </div>

      {error && (
        <div className="dashboard-expiry-error">
          <span>{error}</span>
          <button
            type="button"
            className="dashboard-retry-button"
            onClick={onRetry}
          >
            Tekrar Dene
          </button>
        </div>
      )}

      <div className="dashboard-expiry-grid">
        {cards.map((card) => (
          <div className={`dashboard-expiry-card ${card.key}`} key={card.key}>
            <div className="dashboard-expiry-header">
              <div>
                <div className="dashboard-expiry-count">
                  {loading ? "—" : card.count}
                </div>
                <div className="dashboard-expiry-title">{card.title}</div>
              </div>
              <DashboardIcon
                name={card.key === "expired" ? "alert" : "clock"}
                size={28}
              />
            </div>

            <div className="dashboard-expiry-list">
              {!loading &&
                card.items.slice(0, 4).map((item) => {
                  const departmentName =
                    item.departmentName ||
                    item.department ||
                    "Departman belirtilmedi";

                  const rowContent = (
                    <>
                      <div>
                        <div className="dashboard-expiry-name">
                          {item.title}
                        </div>
                        <div className="dashboard-expiry-department">
                          {departmentName} ·{" "}
                          {formatDashboardDate(item.endDate || item.expiryDate)}
                        </div>
                      </div>

                      <div className="dashboard-expiry-days">
                        {item.daysRemaining < 0
                          ? `${Math.abs(item.daysRemaining)} gün geçti`
                          : item.daysRemaining === 0
                            ? "Bugün doluyor"
                            : `${item.daysRemaining} gün kaldı`}
                      </div>
                    </>
                  );

                  return item.route ? (
                    <button
                      type="button"
                      className="dashboard-expiry-row dashboard-expiry-row-button"
                      key={item.id}
                      onClick={() => onNavigate(item.route)}
                      title={`${departmentName} sayfasını aç`}
                    >
                      {rowContent}
                    </button>
                  ) : (
                    <div className="dashboard-expiry-row" key={item.id}>
                      {rowContent}
                    </div>
                  );
                })}

              {loading && (
                <div className="dashboard-empty">
                  Süreli doküman kayıtları kontrol ediliyor.
                </div>
              )}

              {!loading && !card.items.length && (
                <div className="dashboard-empty">{card.emptyText}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function formatDashboardDate(value) {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function DashboardSection({
  title,
  description,
  items,
  onNavigate,
  priority = false,
  emptyText,
}) {
  return (
    <section className="dashboard-section">
      <div className="dashboard-section-header">
        <div>
          <h2 className="dashboard-section-title">{title}</h2>
          <p className="dashboard-section-description">{description}</p>
        </div>

        <div className="dashboard-section-count">{items.length} MODÜL</div>
      </div>

      {items.length > 0 ? (
        <div
          className={`dashboard-card-grid ${priority ? "priority-grid" : ""}`}
        >
          {items.map((item) => (
            <DashboardCard
              key={item.key || `${item.route}-${item.title}`}
              item={item}
              onClick={() => onNavigate(item.route)}
            />
          ))}
        </div>
      ) : (
        <div className="dashboard-empty">{emptyText}</div>
      )}
    </section>
  );
}

function DashboardCard({ item, onClick }) {
  return (
    <button
      type="button"
      className="dashboard-card"
      onClick={onClick}
      style={{
        "--card-accent": item.accent,
        "--card-soft-color": item.background,
      }}
    >
      <div className="dashboard-card-top">
        <div className="dashboard-card-icon">
          <DashboardIcon name={item.icon} size={23} />
        </div>

        {item.badge && (
          <span className="dashboard-card-badge">{item.badge}</span>
        )}
      </div>

      <div className="dashboard-card-title">{item.title}</div>

      {item.group && <div className="dashboard-card-group">{item.group}</div>}

      <div className="dashboard-card-description">{item.description}</div>

      <div className="dashboard-card-action">
        {item.demo ? "Demoyu Aç" : "Modülü Aç"}
        <DashboardIcon name="arrow" size={15} />
      </div>
    </button>
  );
}

function DashboardIcon({ name, size = 20 }) {
  const commonProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const icons = {
    speed: (
      <>
        <path d="M4 18a8 8 0 1 1 16 0" />
        <path d="M12 18l4-5" />
        <path d="M7 14h.01M17 14h.01M12 10h.01" />
      </>
    ),
    production: (
      <>
        <path d="M3 21V10l6 3V9l6 4V5h6v16H3Z" />
        <path d="M7 17h.01M12 17h.01M17 17h.01" />
      </>
    ),
    maintenance: (
      <>
        <path d="M14.7 6.3a4 4 0 0 0-5-5l2.1 2.1-2.4 2.4-2.1-2.1a4 4 0 0 0 5 5L20 16.4a2.1 2.1 0 0 1-3 3l-7.7-7.7" />
        <path d="m5 19 3-3" />
      </>
    ),
    payment: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 10h18M7 15h3" />
      </>
    ),
    shipment: (
      <>
        <path d="M3 7h11v10H3zM14 10h4l3 3v4h-7z" />
        <circle cx="7" cy="18" r="2" />
        <circle cx="18" cy="18" r="2" />
      </>
    ),
    camera: (
      <>
        <path d="M14.5 5 13 3h-2L9.5 5H5a2 2 0 0 0-2 2v11h18V7a2 2 0 0 0-2-2h-4.5Z" />
        <circle cx="12" cy="12" r="3.5" />
      </>
    ),
    quality: (
      <>
        <path d="M12 3 4 6v6c0 5 3.4 8 8 9 4.6-1 8-4 8-9V6l-8-3Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    approval: (
      <>
        <path d="M9 5h6M9 9h6M9 13h3" />
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <path d="m13 18 2 2 4-4" />
      </>
    ),
    complaint: (
      <>
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" />
        <path d="M12 7v5M12 15h.01" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 2.5 2.5L16 9" />
      </>
    ),
    folder: (
      <path d="M3 6a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6Z" />
    ),
    shared: (
      <>
        <circle cx="7" cy="8" r="3" />
        <circle cx="17" cy="8" r="3" />
        <path d="M2 20a5 5 0 0 1 10 0M12 20a5 5 0 0 1 10 0" />
      </>
    ),
    company: (
      <>
        <path d="M4 21V4h10v17M14 9h6v12M8 8h2M8 12h2M8 16h2M17 13h.01M17 17h.01" />
      </>
    ),
    modules: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    ),
    operations: (
      <>
        <path d="M4 19V5M4 19h16" />
        <path d="m7 15 4-4 3 2 5-6" />
      </>
    ),
    documents: (
      <>
        <path d="M6 2h9l4 4v16H6z" />
        <path d="M14 2v5h5M9 12h6M9 16h6" />
      </>
    ),
    warning: (
      <>
        <path d="M12 3 2.5 20h19L12 3Z" />
        <path d="M12 9v4M12 17h.01" />
      </>
    ),
    alert: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v6M12 17h.01" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    arrow: <path d="m9 18 6-6-6-6" />,
  };

  return <svg {...commonProps}>{icons[name] || icons.modules}</svg>;
}

export default Dashboard;
